import { claude, requireKey, requireKeyAsync, skill } from '../api.js';
import { sectionSummary, applyInstruction } from '../build/coordinator.js';
import { refineDesignTokens, refineDesignVision, buildDesignShell } from '../build/design.js';
import { injectFormMeta } from '../build/export.js';
import {
  buildEditHistory,
  extractSectionHtml,
  replaceSectionInAssembled,
  cleanModelOutput,
  initSection,
  replaceUploadPlaceholders,
  safeStreamHtml,
} from '../build/helpers.js';
import { buildSect } from '../build/pipeline.js';
import { SECT_TYPES } from '../constants.js';
import {
  $,
  assembledForPage,
  finalizeFrame,
  mk,
  patchFrame,
  showToast,
  writeFrame,
} from '../dom.js';
import { getChatExamples, relativeTime } from '../logic/chat.ts';
import { syncFlatTokens } from '../prompts/context.js';
import { curPage, ST } from '../state.js';
import { renderBuildTabContent, renderBuildTabs, renderSList } from './sections.js';

// ── SHARED CHAT COMPONENT ────────────────────────────────────────
// Rendered into a container by Design and Build screens.
// renderChat(containerId, frameId) — renders chat UI
// sendChatMessage(instruction, { frameId }) — edit flow
// undoChat() — full state restore + chatLog cleanup

let ChatContainerId = null;
let chatFrameId = null;

// Scoped textarea lookup — avoids duplicate-ID bugs when multiple chat containers exist
const ChatTa = () => $(ChatContainerId)?.querySelector('.chat-ta');

// ── HELPERS (delegated to logic/chat.ts) ─────────────────────────
const GetChatExamples = () => getChatExamples(curPage().sects.filter(s => s.status === 'done'));

const RelTime = relativeTime;

// ── DOM RENDERING (becomes ChatPanel.vue during migration) ──
// renderChat() — full chat UI with history, input, drag-drop

// Preserve textarea value across re-renders
let ChatDraft = '';

/**
 * Render the chat panel into a container element.
 * Includes conversation history, typing indicator, image attachments,
 * target section pill, input area, and drag-drop handling.
 * @param {string} [containerId] - DOM element ID to render into
 * @param {string} [frameId] - iframe ID for section highlighting
 */
const renderChat = (containerId, frameId) => {
  if (containerId) {
    ChatContainerId = containerId;
  }
  if (frameId) {
    chatFrameId = frameId;
  }
  const c = $(ChatContainerId);
  if (!c) {
    return;
  }
  // Save current textarea value before destroying DOM
  const prevTa = ChatTa();
  if (prevTa) {
    ChatDraft = prevTa.value;
  }
  c.innerHTML = '';

  const pg = curPage();
  const log = pg.chatLog || [];

  // Conversation history
  const hist = mk('div', { class: 'chat-hist' });

  if (!(log.length || ST.isEditing)) {
    // Empty state with capabilities
    const empty = mk('div', { class: 'chat-empty' });
    empty.append(mk('div', { class: 'chat-empty-title' }, 'What would you like to change?'));
    const caps = mk('div', { class: 'chat-caps' });
    const capItems = [
      { icon: '\u270e', text: 'Edit any section' },
      { icon: '\ud83c\udfa8', text: 'Change colors & fonts' },
      { icon: '\u2728', text: 'Suggest improvements' },
      { icon: '\ud83d\uddbc\ufe0f', text: 'Attach images' },
    ];
    capItems.forEach(cap => {
      caps.append(mk('div', { class: 'chat-cap' }, `${cap.icon}\u2003${cap.text}`));
    });
    empty.append(caps);
    const exList = mk('div', { class: 'chat-examples' });
    GetChatExamples().forEach(ex => {
      const item = mk('div', { class: 'chat-ex-item' });
      item.textContent = ex;
      item.onclick = () => {
        const val = ex.replace(/^"|"$/g, '');
        ChatDraft = val;
        const ta = ChatTa();
        if (ta) {
          ta.value = val;
          ta.focus();
        }
      };
      exList.append(item);
    });
    empty.append(exList);
    hist.append(empty);
  }

  log.forEach((entry, i) => {
    if (entry.role === 'user') {
      const row = mk('div', { class: 'chat-msg chat-user' });
      const txt = mk('div', { class: 'chat-msg-text' });
      txt.textContent = entry.text;
      row.append(mk('span', { class: 'chat-role' }, 'You:'), txt);
      if (entry.image) {
        const thumb = mk('img', {
          class: 'chat-img-thumb',
          src: entry.image.preview,
          alt: 'Attached',
        });
        row.append(thumb);
      }
      if (entry.ts) {
        row.append(mk('span', { class: 'chat-ts' }, RelTime(entry.ts)));
      }
      hist.append(row);
    } else if (entry.role === 'ai') {
      const row = mk('div', { class: 'chat-msg chat-ai' });
      const scopeMeta = {
        clarify: { icon: '?', label: 'Question', cls: 'scope-clarify' },
        tokens: { icon: '\u25cf', label: 'tokens', cls: 'scope-tokens' },
        vision: { icon: '\u25c6', label: 'vision', cls: 'scope-vision' },
        global: { icon: '\u2b24', label: 'global', cls: 'scope-global' },
        add: { icon: '\uff0b', label: 'added', cls: 'scope-add' },
        remove: { icon: '\u2212', label: 'removed', cls: 'scope-remove' },
      };
      const meta = scopeMeta[entry.scope] || {
        icon: '\u2713',
        label: entry.scope,
        cls: 'scope-section',
      };
      if (entry.scope === 'clarify') {
        row.append(mk('span', { class: `chat-scope-icon ${meta.cls}` }, meta.icon));
        row.append(mk('span', { class: 'chat-applied' }, entry.applied));
      } else {
        row.append(mk('span', { class: `chat-scope-icon ${meta.cls}` }, meta.icon));
        row.append(mk('span', { class: `chat-scope ${meta.cls}` }, `${meta.label}:`));
        row.append(mk('span', { class: 'chat-applied' }, entry.applied || 'Applied'));
      }
      // Action buttons for non-clarification entries
      if (entry.scope !== 'clarify') {
        // Copy summary
        if (entry.applied) {
          const copyBtn = mk(
            'button',
            {
              class: 'chat-copy-btn',
              title: 'Copy',
              onclick() {
                navigator.clipboard.writeText(entry.applied).then(() => {
                  copyBtn.textContent = '\u2713';
                  setTimeout(() => {
                    copyBtn.textContent = '\u2398';
                  }, 1200);
                });
              },
            },
            '\u2398',
          );
          row.append(copyBtn);
        }
        if (i === LastAiIdx(log)) {
          const undoBtn = mk(
            'button',
            { class: 'chat-undo-btn', onclick: undoChat },
            '\u21a9 Undo',
          );
          row.append(undoBtn);
        }
      }
      // Timestamp
      if (entry.ts) {
        row.append(mk('span', { class: 'chat-ts' }, RelTime(entry.ts)));
      }

      // Hover AI message → highlight section in iframe
      const scopeIds =
        entry.scope &&
        !['clarify', 'tokens', 'vision', 'global', 'add', 'remove'].includes(entry.scope)
          ? entry.scope.split(',')
          : [];
      if (scopeIds.length) {
        row.addEventListener('mouseenter', () => {
          scopeIds.forEach(id => {
            const el = $(chatFrameId)?.contentDocument?.getElementById(id);
            if (el) {
              el.style.outline = '2px dashed rgba(99,102,241,0.5)';
              el.style.outlineOffset = '-2px';
            }
          });
        });
        row.addEventListener('mouseleave', () => {
          scopeIds.forEach(id => {
            const el = $(chatFrameId)?.contentDocument?.getElementById(id);
            if (el) {
              el.style.outline = '';
              el.style.outlineOffset = '';
            }
          });
        });
      }

      hist.append(row);

      // Follow-up chips on last AI message (not clarifications, not while editing)
      if (entry.scope !== 'clarify' && i === LastAiIdx(log) && !ST.isEditing) {
        const chips = mk('div', { class: 'chat-followups' });
        // Suggest improvements for the edited section
        const editedId =
          entry.scope && !['tokens', 'vision', 'global', 'add', 'remove'].includes(entry.scope)
            ? entry.scope.split(',')[0]
            : null;
        if (editedId) {
          const sect = pg.sects.find(s => s.id === editedId);
          if (sect?.status === 'done') {
            chips.append(
              mk(
                'button',
                {
                  class: 'chat-followup',
                  onclick() {
                    const idx = pg.sects.indexOf(sect);
                    if (idx >= 0) {
                      triggerSuggest(idx);
                    }
                  },
                },
                '\u2728 Suggest',
              ),
            );
          }
        }
        // Before/after comparison toggle
        if (pg.editUndoStack.length) {
          let ShowingBefore = false;
          const toggleBtn = mk(
            'button',
            {
              class: 'chat-followup',
              onclick() {
                const snap = pg.editUndoStack[pg.editUndoStack.length - 1];
                if (!snap) {
                  return;
                }
                ShowingBefore = !ShowingBefore;
                if (ShowingBefore) {
                  writeFrame(chatFrameId, snap.html);
                  toggleBtn.textContent = '\u25c0 Show after';
                  toggleBtn.classList.add('active');
                } else {
                  writeFrame(chatFrameId, pg.assembled);
                  toggleBtn.textContent = '\u25b6 Before / After';
                  toggleBtn.classList.remove('active');
                }
              },
            },
            '\u25b6 Before / After',
          );
          chips.append(toggleBtn);
          chips.append(mk('button', { class: 'chat-followup', onclick: undoChat }, '\u21a9 Undo'));
        }
        hist.append(chips);
      }
    } else if (entry.role === 'error') {
      const row = mk('div', { class: 'chat-msg chat-error' });
      row.append(mk('span', { class: 'chat-error-icon' }, '\u26a0'));
      row.append(mk('span', { class: 'chat-error-text' }, entry.text));
      if (entry.originalInstr) {
        const retry = mk(
          'button',
          {
            class: 'chat-retry-btn',
            onclick() {
              ChatDraft = entry.originalInstr;
              if (entry.targetId) {
                ST.chatTargetSection = entry.targetId;
              }
              const ta = ChatTa();
              if (ta) {
                ta.value = entry.originalInstr;
                ta.focus();
              }
            },
          },
          'Retry',
        );
        row.append(retry);
      }
      hist.append(row);
    } else if (entry.role === 'suggesting') {
      // Loading state while AI analyzes
      const row = mk('div', { class: 'chat-msg chat-suggesting' });
      row.append(
        mk('span', { class: 'chat-suggest-icon' }, '\u2728'),
        mk('span', { class: 'chat-suggest-loading' }, `Analyzing ${entry.sectionTitle}\u2026`),
      );
      const dots = mk('div', { class: 'chat-typing' });
      dots.append(mk('span'), mk('span'), mk('span'));
      row.append(dots);
      hist.append(row);
    } else if (entry.role === 'suggestions') {
      const row = mk('div', { class: 'chat-msg chat-suggestions' });

      if (entry.suggestions?.length) {
        row.append(
          mk('div', { class: 'chat-suggest-header' }, `Improvements for ${entry.sectionTitle}:`),
        );

        const grid = mk('div', { class: 'chat-suggest-grid' });

        entry.suggestions.forEach(s => {
          const card = mk('div', { class: 'chat-suggest-card' });

          // Highlight + scroll to section in iframe on hover
          card.addEventListener('mouseenter', () => {
            const el = $(chatFrameId)?.contentDocument?.getElementById(entry.sectionId);
            if (el) {
              el.style.outline = '2px dashed rgba(99,102,241,0.5)';
              el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          });
          card.addEventListener('mouseleave', () => {
            const el = $(chatFrameId)?.contentDocument?.getElementById(entry.sectionId);
            if (el) {
              el.style.outline = '';
            }
          });

          // Category pill
          const catColors = {
            conversion: '#10b981',
            design: '#6366f1',
            content: '#f59e0b',
            ux: '#3b82f6',
            accessibility: '#8b5cf6',
          };
          const catColor = catColors[s.category] || '#64748b';
          card.append(
            mk(
              'span',
              {
                class: 'chat-suggest-cat',
                style: `background:${catColor}15;color:${catColor}`,
              },
              s.category,
            ),
          );

          // Title + description
          card.append(mk('div', { class: 'chat-suggest-title' }, s.title));
          card.append(mk('div', { class: 'chat-suggest-desc' }, s.description));

          // Actions
          const actions = mk('div', { class: 'chat-suggest-actions' });
          actions.append(
            mk(
              'button',
              {
                class: 'chat-suggest-apply',
                onclick(e) {
                  e.stopPropagation();
                  ST.chatTargetSection = entry.sectionId;
                  ChatDraft = '';
                  requireKey(() => {
                    void sendChatMessage(s.instruction, { frameId: chatFrameId });
                  });
                },
              },
              'Apply',
            ),
          );
          actions.append(
            mk(
              'button',
              {
                class: 'chat-suggest-customize',
                onclick(e) {
                  e.stopPropagation();
                  ST.chatTargetSection = entry.sectionId;
                  ChatDraft = s.instruction;
                  const ta = ChatTa();
                  if (ta) {
                    ta.value = s.instruction;
                    ta.focus();
                  }
                  renderChat();
                },
              },
              'Customize',
            ),
          );

          card.append(actions);
          grid.append(card);
        });

        // Re-analyze button
        const refreshBtn = mk(
          'button',
          {
            class: 'chat-suggest-refresh',
            onclick(e) {
              e.stopPropagation();
              // Clear cache and re-trigger
              const sect = curPage().sects.find(s => s.id === entry.sectionId);
              if (sect) {
                sect.suggestions = null;
                sect.screenshotCache = null;
              }
              const sectIdx = curPage().sects.findIndex(s => s.id === entry.sectionId);
              if (sectIdx >= 0) {
                triggerSuggest(sectIdx);
              }
            },
          },
          '\u21bb Re-analyze',
        );
        grid.append(refreshBtn);

        row.append(grid);
        hist.append(row);
      } else {
        row.append(
          mk('span', { class: 'chat-check' }, '\u2713'),
          mk(
            'span',
            { class: 'chat-applied' },
            `${entry.sectionTitle} looks great \u2014 no improvements needed.`,
          ),
        );
        hist.append(row);
      }
    }
  });

  // Typing indicator with edit status
  if (ST.isEditing) {
    const typing = mk('div', { class: 'chat-typing' });
    if (ST.editingStatus) {
      typing.append(mk('span', { class: 'chat-edit-status' }, ST.editingStatus));
    }
    typing.append(mk('span'), mk('span'), mk('span'));
    hist.append(typing);
  }

  // Clear chat button at bottom (when there are messages and not editing)
  if (log.length > 2 && !ST.isEditing) {
    const clearWrap = mk('div', { class: 'chat-clear-wrap' });
    clearWrap.append(
      mk(
        'button',
        {
          class: 'chat-clear-btn',
          onclick() {
            pg.chatLog = [];
            renderChat();
          },
        },
        '\u2715 Clear chat',
      ),
    );
    hist.append(clearWrap);
  }

  c.append(hist);

  // Image attachment bar
  if (ST.chatImage) {
    const bar = mk('div', { class: 'chat-img-bar' });
    const preview = mk('img', {
      class: 'chat-img-preview',
      src: ST.chatImage.preview,
      alt: 'Attached',
    });
    const info = mk('div', { class: 'chat-img-info' });
    info.append(mk('span', { class: 'chat-img-name' }, ST.chatImage.name));
    const modeWrap = mk('div', { class: 'chat-img-modes' });
    const matchBtn = mk(
      'button',
      {
        class: `chat-img-mode${ST.chatImage.mode === 'match' ? ' active' : ''}`,
        onclick() {
          ST.chatImage.mode = 'match';
          renderChat();
        },
      },
      'Match layout',
    );
    const useBtn = mk(
      'button',
      {
        class: `chat-img-mode${ST.chatImage.mode === 'use' ? ' active' : ''}`,
        onclick() {
          ST.chatImage.mode = 'use';
          renderChat();
        },
      },
      'Use as image',
    );
    modeWrap.append(matchBtn, useBtn);
    info.append(modeWrap);
    const removeBtn = mk(
      'button',
      {
        class: 'chat-img-remove',
        onclick() {
          ST.chatImage = null;
          renderChat();
        },
      },
      '\u00d7',
    );
    bar.append(preview, info, removeBtn);
    c.append(bar);
  }

  // Target section pill with quick actions
  const inputArea = mk('div', { class: 'chat-input-area' });
  if (ST.chatTargetSection) {
    const targetSect = pg.sects.find(s => s.id === ST.chatTargetSection);
    const targetTitle = targetSect?.title || ST.chatTargetSection;
    const pill = mk('div', { class: 'chat-target-pill' });
    pill.append(
      mk('span', { class: 'chat-target-name' }, `\u2726 ${targetTitle}`),
      mk(
        'button',
        {
          class: 'chat-target-clear',
          onclick() {
            ST.chatTargetSection = null;
            renderChat();
          },
        },
        '\u00d7',
      ),
    );
    inputArea.append(pill);

    // Quick action buttons for targeted section
    if (targetSect?.status === 'done' && !ST.isEditing && !ST.running) {
      const qActions = mk('div', { class: 'chat-target-actions' });
      const sectIdx = pg.sects.indexOf(targetSect);
      qActions.append(
        mk(
          'button',
          {
            class: 'chat-target-act',
            onclick() {
              triggerSuggest(sectIdx);
            },
          },
          '\u2728 Suggest',
        ),
      );
      qActions.append(
        mk(
          'button',
          {
            class: 'chat-target-act',
            onclick() {
              ST.expanded = sectIdx;
              ST.chatTab = 'sections';
              renderBuildTabs();
              renderBuildTabContent();
              renderSList();
            },
          },
          '\u2630 Details',
        ),
      );
      inputArea.append(qActions);
    }
  }

  // Input row
  const inputRow = mk('div', { class: 'chat-input-row' });
  const ta = mk('textarea', {
    class: 'chat-ta',
    rows: '2',
    placeholder: ST.running ? 'Building\u2026 wait to chat' : 'Describe your change\u2026',
  });
  ta.disabled = false; // guards in _triggerSend/sendChatMessage handle busy state
  ta.value = ChatDraft;
  ta.addEventListener('keydown', e => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      TriggerSend();
    }
    // Up arrow in empty textarea → recall last instruction
    if (e.key === 'ArrowUp' && !ta.value.trim()) {
      const lastUser = [...(pg.chatLog || [])].reverse().find(m => m.role === 'user');
      if (lastUser) {
        ta.value = lastUser.text;
        ChatDraft = lastUser.text;
        e.preventDefault();
      }
    }
  });
  // Keep draft in sync + auto-grow
  const AutoGrow = () => {
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  };
  ta.addEventListener('input', () => {
    ChatDraft = ta.value;
    AutoGrow();
  });
  // Initial size if draft has content
  if (ChatDraft) {
    setTimeout(AutoGrow, 0);
  }

  // Paperclip for image attachment
  const clipBtn = mk('button', { class: 'chat-clip-btn', title: 'Attach image' });
  clipBtn.innerHTML = '\uD83D\uDCCE';
  const fileInp = mk('input', { type: 'file', accept: 'image/*', style: 'display:none' });
  fileInp.addEventListener('change', e => {
    const f = e.target.files[0];
    if (!f) {
      return;
    }
    const r = new FileReader();
    r.onload = ev => {
      ST.chatImage = {
        data: ev.target.result.split(',')[1],
        type: f.type,
        name: f.name,
        preview: ev.target.result,
        mode: 'match',
      };
      renderChat();
      ChatTa()?.focus();
    };
    r.readAsDataURL(f);
    e.target.value = '';
  });
  clipBtn.onclick = () => fileInp.click();

  const sendBtn = mk(
    'button',
    {
      class: 'chat-send-btn',
      onclick: TriggerSend,
    },
    '\u2192',
  );

  // Keyboard shortcut hint
  const hint = mk(
    'div',
    { class: 'chat-hint' },
    navigator.platform?.includes('Mac') ? '\u2318\u21a9 to send' : 'Ctrl+\u21a9 to send',
  );
  inputRow.append(ta, fileInp, clipBtn, sendBtn);
  inputArea.append(inputRow, hint);
  c.append(inputArea);

  // Drag-and-drop image
  const HandleDrop = file => {
    if (!file?.type?.startsWith('image/')) {
      return;
    }
    const r = new FileReader();
    r.onload = ev => {
      ST.chatImage = {
        data: ev.target.result.split(',')[1],
        type: file.type,
        name: file.name,
        preview: ev.target.result,
        mode: 'match',
      };
      renderChat();
      ChatTa()?.focus();
    };
    r.readAsDataURL(file);
  };
  c.addEventListener('dragover', e => {
    e.preventDefault();
    c.classList.add('chat-dragover');
  });
  c.addEventListener('dragleave', e => {
    if (!c.contains(e.relatedTarget)) {
      c.classList.remove('chat-dragover');
    }
  });
  c.addEventListener('drop', e => {
    e.preventDefault();
    c.classList.remove('chat-dragover');
    const file = e.dataTransfer?.files?.[0];
    if (file) {
      HandleDrop(file);
    }
  });

  // Auto-scroll history to bottom
  setTimeout(() => {
    hist.scrollTop = hist.scrollHeight;
  }, 10);
};

// Find index of last AI entry in log
const LastAiIdx = log => {
  for (let i = log.length - 1; i >= 0; i--) {
    if (log[i].role === 'ai' && log[i].scope !== 'clarify') {
      return i;
    }
  }
  return -1;
};

const TriggerSend = () => {
  const ta = ChatTa();
  const text = ta?.value?.trim() || '';
  if (!text && !ST.chatImage) {
    return;
  }
  if (ta) ta.value = '';
  ChatDraft = '';
  requireKey(() => {
    void sendChatMessage(text, { frameId: chatFrameId });
  });
};

// ── SCREENSHOT CAPTURE ──────────────────────────────────────────
/**
 * Capture a screenshot of a single section using CF Browser Rendering.
 * Returns base64 JPEG string. Uses cache if available.
 */
/**
 * Capture a screenshot of a single section using CF Browser Rendering.
 * Returns base64 JPEG string. Uses cache if available.
 * @param {string} sectionId
 * @param {boolean} [force=false] - Bypass cache
 * @returns {Promise<string|null>} Base64 JPEG or null
 */
const captureSection = async (sectionId, force = false) => {
  const pg = curPage();
  const sect = pg.sects.find(s => s.id === sectionId);
  if (!sect?.html) {
    return null;
  }

  // Return cache if valid
  if (!force && sect.screenshotCache) {
    return sect.screenshotCache;
  }

  // Build minimal HTML: shell head + this section only
  const shellParts = pg.shellHtml.split('<!-- SF_SECTIONS -->');
  if (shellParts.length < 2) {
    return null;
  }

  // Find scoped style for this section from assembled page
  const extracted = extractSectionHtml(pg.assembled, sectionId);
  let actualId = sectionId;
  let sectionContent = '';
  if (extracted) {
    sectionContent = `${extracted.sectionStyle}\n${extracted.sectionHtml}`;
  } else {
    const idMatch = sect.html.match(/<(?:section|header|footer)[^>]+id=["']([^"']+)["']/);
    if (idMatch) {
      actualId = idMatch[1];
    }
    sectionContent = sect.html;
  }

  // Replace upload placeholders with actual data URLs
  sectionContent = replaceUploadPlaceholders(sectionContent);

  const minimalHtml = `${shellParts[0]}${sectionContent}${shellParts[1]}`;

  try {
    const res = await fetch('/api/screenshot', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        html: minimalHtml,
        selector: `#${actualId}`,
        viewport: { width: 1280, height: 900 },
      }),
    });
    if (!res.ok) {
      console.warn('Screenshot capture failed:', res.status);
      return null;
    }
    const blob = await res.blob();
    const base64 = await new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.readAsDataURL(blob);
    });
    // Cache on section object
    sect.screenshotCache = base64;
    sect.screenshotTs = Date.now();
    return base64;
  } catch (e) {
    console.warn('Screenshot capture error:', e);
    return null;
  }
};

// ── SECTION EDIT (moved from preview.js) ──────────────────────────
/**
 * Apply an edit instruction to a single section.
 * Captures screenshot for visual context, sends to Claude edit-section skill,
 * streams partial updates, and returns new assembled HTML + summary.
 * @param {string} instr - Edit instruction
 * @param {string} sectionId - Target section ID
 * @param {string} prevHtml - Full page HTML before edit
 * @param {Function} [onChunk] - Streaming callback for partial HTML
 * @returns {Promise<{html: string, summary: string}>}
 */
const applySectionEdit = async (instr, sectionId, prevHtml, onChunk) => {
  // Try exact ID match first; if not found, the AI may have generated a different ID
  // in the HTML — find the actual element ID from sect.html
  let actualId = sectionId;
  let extracted = extractSectionHtml(prevHtml, sectionId);
  if (!extracted) {
    const pg = curPage();
    const sect = pg.sects.find(s => s.id === sectionId);
    if (sect?.html) {
      const idMatch = sect.html.match(/<(?:section|header|footer)[^>]+id=["']([^"']+)["']/);
      if (idMatch) {
        actualId = idMatch[1];
        extracted = extractSectionHtml(prevHtml, actualId);
      }
    }
  }
  if (!extracted) {
    throw new Error(`Section ${sectionId} not found in page`);
  }

  const pg = curPage();
  const sect = pg.sects.find(s => s.id === sectionId);
  const historyBlock = sect ? buildEditHistory(sect, instr) : '';

  // Start screenshot capture in parallel with prompt construction
  const screenshotPromise = captureSection(sectionId);

  const sk = skill('edit-section');
  const sectionContent = `${extracted.sectionStyle}\n${extracted.sectionHtml}`;

  // Await screenshot (usually done by now — prompt construction takes longer)
  const screenshot = await screenshotPromise;
  const editImgs = screenshot
    ? [{ data: screenshot, type: 'image/jpeg', label: `Current screenshot of section #${actualId} — live rendered state. Apply the edit while preserving composition.` }]
    : [];

  // Include user-attached replace image as vision input
  let replaceImgCtx = '';
  if (sect?.replaceImg?.data) {
    editImgs.push({
      data: sect.replaceImg.data,
      type: sect.replaceImg.type || 'image/jpeg',
      label: 'User-provided image — use as directed in the instruction',
    });
    const placeholder = `__REPLACE_IMG_${sectionId}__`;
    replaceImgCtx = `<replace_image>A user-provided image is attached. Use this URL as the src attribute: ${placeholder}</replace_image>\n\n`;
    ST.uploadImgMap = null; // invalidate so placeholder resolves
  }

  const screenshotCtx = screenshot
    ? '<screenshot_context>A screenshot of the current section is provided. Use it to understand the visual state — layout, spacing, colors as rendered. The HTML source is the ground truth for what to edit.</screenshot_context>\n\n'
    : '';

  let lastReassembled = 0;
  const newContent = await claude({
    sys: sk.prompt || '',
    user: `${screenshotCtx}${replaceImgCtx}${
      historyBlock ? `${historyBlock}\n\n` : ''
    }<current_section>\n${sectionContent}\n</current_section>\n\n<instruction>${instr}</instruction>\n\nBegin your response with a one-line HTML comment summarizing what you changed:\n<!-- APPLIED: description of changes -->\nThen output the section HTML.`,
    imgs: editImgs,
    tokens: sk.tokens || 6000,
    model: sk.model,
    temp: sk.temp,
    effort: sk.effort,
    onChunk(t) {
      if (onChunk) {
        const now = Date.now();
        if (now - lastReassembled > 300) {
          lastReassembled = now;
          const partial = replaceSectionInAssembled(prevHtml, actualId, safeStreamHtml(t));
          onChunk(partial);
        }
      }
    },
  });

  // Parse APPLIED comment for summary
  const applyMatch = newContent.match(/<!--\s*APPLIED:\s*(.+?)\s*-->/);
  const summary = applyMatch?.[1] || '';
  const cleanContent = cleanModelOutput(newContent.replace(/<!--\s*APPLIED:.*?-->\s*/, ''));

  // Update sect.html
  let finalContent = cleanContent;
  if (finalContent.includes('<form')) {
    finalContent = injectFormMeta(finalContent, 'contact');
  }
  if (sect) {
    sect.html = finalContent;
    sect.editLog ??= [];
    sect.editLog.push({ instr, applied: summary });
    // Invalidate caches after edit
    sect.screenshotCache = null;
    sect.suggestions = null;
  }

  return { html: replaceSectionInAssembled(prevHtml, actualId, finalContent), summary };
};

// ── COORDINATOR CALLBACKS ────────────────────────────────────────

/** Add a new section to the current page at the right position. */
const chatAddSection = (typeId, afterId = null, instruction = '') => {
  const pg = curPage();
  const def = SECT_TYPES.find(t => t.id === typeId) || { id: typeId, color: '#64748b', desc: '' };
  const title = def.id.charAt(0).toUpperCase() + def.id.slice(1).replace(/-/g, ' ');
  const planned = (ST.originalConceptSections || []).find(s => s.type === typeId);
  const newSect = initSection({
    type: def.id,
    title: planned?.title || title,
    purpose: planned?.purpose || def.desc,
    visualBrief: planned?.visualBrief || '',
    copyNotes: planned?.copyNotes || '',
    layout: planned?.layout || 'auto',
    bgStyle: planned?.bgStyle || 'light',
  }, pg.sects);
  if (instruction) newSect.note = instruction;
  const insertIdx = afterId
    ? pg.sects.findIndex(s => s.id === afterId) + 1
    : (() => {
        const fi = pg.sects.findIndex(s => s.type === 'footer');
        return fi >= 0 ? fi : pg.sects.length;
      })();
  pg.sects.splice(insertIdx, 0, newSect);
  // Sync to concept.sections
  ST.concept.sections.splice(insertIdx, 0, {
    id: newSect.id,
    type: newSect.type,
    title: newSect.title,
    purpose: newSect.purpose,
    visualBrief: newSect.visualBrief || '',
    layout: newSect.layout || 'auto',
    bgStyle: newSect.bgStyle || 'light',
    copyNotes: newSect.copyNotes || '',
  });
  return newSect;
};

/** Remove a section by ID from the current page. */
const chatRemoveSection = id => {
  const pg = curPage();
  const idx = pg.sects.findIndex(s => s.id === id);
  if (idx < 0) {
    return;
  }
  pg.sects.splice(idx, 1);
  const cIdx = ST.concept.sections.findIndex(s => s.id === id);
  if (cIdx >= 0) {
    ST.concept.sections.splice(cIdx, 1);
  }
  pg.edited = false;
  pg.assembled = assembledForPage(pg);
};

/** Rebuild a section from scratch with an optional instruction override. */
const rebuildSection = async (id, instruction) => {
  const pg = curPage();
  const idx = pg.sects.findIndex(s => s.id === id);
  if (idx < 0) {
    return;
  }
  const sect = pg.sects[idx];
  if (instruction?.trim()) {
    sect.editLog ??= [];
    sect.editLog.push({ instr: instruction, applied: '' });
  }
  sect.html = '';
  sect.status = 'pending';
  sect.screenshotCache = null;
  sect.suggestions = null;
  pg.edited = false;
  await buildSect(idx, pg, { tempNote: instruction, previewFrame: chatFrameId });
  pg.assembled = assembledForPage(pg);
  return pg.assembled;
};

// ── SUGGEST IMPROVEMENTS ────────────────────────────────────────
/** JSON Schema for structured suggestion output from Claude. */
const SUGGEST_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  properties: {
    suggestions: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: false,
        properties: {
          title: { type: 'string', description: 'Short improvement name (3-6 words)' },
          description: {
            type: 'string',
            description: 'What changes and why it helps (1 sentence)',
          },
          instruction: {
            type: 'string',
            description: 'Exact edit instruction to send to the editor AI',
          },
          category: {
            type: 'string',
            enum: ['conversion', 'design', 'content', 'ux', 'accessibility'],
          },
        },
        required: ['title', 'description', 'instruction', 'category'],
      },
    },
  },
  required: ['suggestions'],
};

/** System prompt for the suggest-improvements AI analysis. */
const SUGGEST_PROMPT = `<role>
You are a conversion-focused web design consultant analyzing a live website section.
</role>

<task>
Analyze the provided section (screenshot if available, plus HTML source). Suggest 4-6 specific, actionable improvements.
Each suggestion must have an "instruction" field — the exact edit command that would implement it.

Focus on improvements that:
1. Increase conversion (CTAs, social proof, urgency, clarity)
2. Improve visual hierarchy and readability
3. Fix design issues (spacing, contrast, alignment, responsiveness)
4. Enhance UX (interactions, accessibility, mobile experience)
</task>

<rules>
- Be specific: "Add a trust badge row showing '500+ companies' below the hero headline" not "Add social proof"
- Each instruction must be self-contained — it will be sent to an AI editor with only the section HTML context
- Don't suggest adding entirely new sections — focus on improving THIS section
- Don't suggest changes that would require backend work or new data
- Consider what other sections exist on the page to avoid duplication
- Prioritize high-impact, low-effort improvements first
- If the section already looks polished, suggest subtle refinements rather than major overhauls
</rules>`;

/**
 * Analyze a built section and return improvement suggestions.
 * Captures a screenshot, extracts HTML, and sends to Claude for analysis.
 * Results are cached on the section object.
 * @param {string} sectionId
 * @returns {Promise<Array>} Array of suggestion objects
 */
const suggestImprovements = async sectionId => {
  const pg = curPage();
  const sect = pg.sects.find(s => s.id === sectionId);
  if (!sect?.html || sect.status !== 'done') {
    return [];
  }

  // Check cache
  if (sect.suggestions?.length) {
    return sect.suggestions;
  }

  // Capture screenshot
  const screenshot = await captureSection(sectionId);
  const imgs = screenshot
    ? [{ data: screenshot, type: 'image/jpeg', label: `Current screenshot of section #${sectionId} — analyze for improvements` }]
    : [];

  // Extract section HTML from assembled page
  const extracted = extractSectionHtml(pg.assembled, sectionId);
  let sectionHtml = '';
  if (extracted) {
    sectionHtml = `${extracted.sectionStyle}\n${extracted.sectionHtml}`;
  } else {
    const idMatch = sect.html.match(/<(?:section|header|footer)[^>]+id=["']([^"']+)["']/);
    if (idMatch) {
      const alt = extractSectionHtml(pg.assembled, idMatch[1]);
      if (alt) {
        sectionHtml = `${alt.sectionStyle}\n${alt.sectionHtml}`;
      }
    }
  }
  if (!sectionHtml) {
    sectionHtml = sect.html;
  }

  // Build context about existing sections (so AI doesn't suggest duplicates)
  const siblings = pg.sects
    .filter(s => s.id !== sectionId && s.status === 'done')
    .map(s => `- ${s.type}: ${s.title}`)
    .join('\n');

  // Marketing brief excerpt for conversion context
  const mb = ST.marketingBrief;
  const briefSnippet = mb
    ? [
        mb.targetAudience && `Audience: ${mb.targetAudience}`,
        mb.desiredAction && `Goal: ${mb.desiredAction}`,
        mb.differentiator && `Differentiator: ${mb.differentiator}`,
      ]
        .filter(Boolean)
        .join('\n')
    : '';

  const screenshotNote = screenshot
    ? 'A screenshot of the rendered section is attached. Use it alongside the HTML to identify visual issues (spacing, contrast, alignment, hierarchy).'
    : 'No screenshot available — analyze the HTML structure and styles to infer visual issues.';

  const user = `<section_html>
${sectionHtml}
</section_html>

<section_meta>
Type: ${sect.type}
Title: ${sect.title}
Purpose: ${sect.purpose || ''}
</section_meta>

${siblings ? `<other_sections>\n${siblings}\n</other_sections>` : ''}

${briefSnippet ? `<business_context>\n${briefSnippet}\n</business_context>` : ''}

${screenshotNote}

Analyze this section and suggest improvements.`;

  const result = await claude({
    sys: SUGGEST_PROMPT,
    user,
    imgs,
    tokens: 1500,
    model: 'claude-sonnet-4-6',
    temp: 0.4,
    effort: 'medium',
    schema: SUGGEST_SCHEMA,
  });

  const parsed = typeof result === 'string' ? JSON.parse(result) : result;
  const suggestions = parsed.suggestions || [];

  // Cache
  sect.suggestions = suggestions;
  return suggestions;
};

/**
 * Trigger the suggest-improvements flow for a section.
 * Switches to chat tab, shows loading state, runs analysis, and renders results.
 * @param {number} sectionIdx - Index in curPage().sects
 */
const triggerSuggest = async sectionIdx => {
  const pg = curPage();
  const sect = pg.sects[sectionIdx];
  if (!sect || sect.status !== 'done') {
    return;
  }
  if (ST.isEditing || ST.running) {
    showToast('Wait for the current operation to finish.');
    return;
  }

  // Switch to chat tab
  ST.chatTab = 'chat';
  ST.chatTargetSection = sect.id;
  renderBuildTabs();
  renderBuildTabContent();

  // Add loading message to chat
  pg.chatLog ??= [];
  const loadingEntry = {
    role: 'suggesting',
    sectionId: sect.id,
    sectionTitle: sect.title,
    ts: Date.now(),
  };
  pg.chatLog.push(loadingEntry);
  renderChat();

  try {
    await requireKeyAsync();
    const suggestions = await suggestImprovements(sect.id);

    // Replace loading entry with suggestions
    const idx = pg.chatLog.indexOf(loadingEntry);
    if (idx >= 0) {
      pg.chatLog[idx] = {
        role: 'suggestions',
        sectionId: sect.id,
        sectionTitle: sect.title,
        suggestions,
        ts: Date.now(),
      };
    }
  } catch (e) {
    // Replace loading with error
    const idx = pg.chatLog.indexOf(loadingEntry);
    if (idx >= 0) {
      pg.chatLog[idx] = {
        role: 'error',
        text: `Failed to analyze ${sect.title}: ${e.message}`,
        originalInstr: `suggest improvements for ${sect.title}`,
        ts: Date.now(),
      };
    }
  }

  renderChat();
};

// ── CHAT LOGIC (extract to composable during Vue migration) ──
// sendChatMessage() — coordinator dispatch + state management
// undoChat() — full state restore

/**
 * Send a chat message through the coordinator for routing and execution.
 * Handles undo snapshots, section targeting, image attachments,
 * streaming updates, and error recovery.
 * @param {string} instruction - User's edit instruction
 * @param {{frameId: string}} options - iframe ID for live preview
 */
const sendChatMessage = async (instruction, { frameId }) => {
  // Auto-generate instruction when image is attached with no text
  if (!instruction.trim() && ST.chatImage && ST.chatTargetSection) {
    const sect = curPage().sects.find(s => s.id === ST.chatTargetSection);
    const label = sect?.title || ST.chatTargetSection;
    instruction = ST.chatImage.mode === 'use'
      ? `Use the attached image in the ${label} section`
      : `Match the style of the attached image in the ${label} section`;
  }
  if (!instruction.trim() || ST.isEditing) {
    return;
  }
  if (ST.running) {
    showToast('Wait for the current build to finish, then try again.');
    return;
  }

  const pg = curPage();

  // 1. Resolve image + target
  const image = ST.chatImage;
  let targetId = ST.chatTargetSection;
  if (image && targetId) {
    const sect = pg.sects.find(s => s.id === targetId);
    if (sect) {
      if (image.mode === 'match') {
        sect.screenshotImg = image;
      } else {
        sect.replaceImg = image;
        ST.uploadImgMap = null; // invalidate so placeholder resolves
      }
    }
  }

  // 2. Add user message to chat log
  pg.chatLog ??= [];
  const chatLogLenBeforeMsg = pg.chatLog.length; // capture BEFORE push for clean undo
  pg.chatLog.push({
    role: 'user',
    text: instruction,
    ts: Date.now(),
    targetId: targetId || null,
    image: image ? { preview: image.preview, mode: image.mode, targetId } : null,
  });
  ST.chatImage = null;
  ST.chatTargetSection = null;
  renderChat();

  // 3. Prepare deep undo snapshot
  const prevHtml = pg.assembled;
  pg.editUndoStack.push({
    html: prevHtml,
    shellHtml: pg.shellHtml,
    sects: structuredClone(pg.sects),
    conceptSections: structuredClone(ST.concept.sections),
    tokens: structuredClone(ST.concept.designTokens),
    vision: structuredClone(ST.concept.designVision),
    colors: { ...ST.concept.colors },
    fonts: { ...ST.concept.fonts },
    chatLogLen: chatLogLenBeforeMsg,
  });
  if (pg.editUndoStack.length > 10) {
    pg.editUndoStack.shift();
  }

  // 4. Large page warning
  const WARN_THRESHOLD = 150_000;
  if (prevHtml && prevHtml.length > WARN_THRESHOLD) {
    const approxK = Math.round(prevHtml.length / 3.5 / 1000);
    if (
      !confirm(`Your page is very large (~${approxK}K tokens). Edits may be unreliable. Continue?`)
    ) {
      pg.editUndoStack.pop();
      pg.chatLog.pop();
      ST.isEditing = false;
      renderChat();
      return;
    }
  }

  // 5. Execute via coordinator
  ST.isEditing = true;
  ST.editingStatus = 'Routing\u2026';
  let latest = '';
  let lastPatched = '';
  const tick = setInterval(() => {
    if (latest && latest !== lastPatched) {
      lastPatched = latest;
      patchFrame(frameId, latest);
    }
  }, 300);
  if (!ST.activeStreamTicks) {
    ST.activeStreamTicks = [];
  }
  ST.activeStreamTicks.push(tick);
  renderChat(); // show typing indicator

  try {
    const sections = pg.sects
      .filter(s => s.html)
      .map(s => ({
        id: s.id,
        type: s.type,
        title: s.title,
        bgStyle: s.bgStyle,
        status: s.status,
        content: sectionSummary(s.html),
      }));
    const validIds = pg.sects.filter(s => s.html).map(s => s.id);
    const globalHistory = pg.editHist.filter(e => e.scope === 'global').map(e => e.instr);
    const recentChat = pg.chatLog.slice(0, -1); // exclude the message we just pushed — it's sent as <instruction>

    // If answering a clarification, combine with the original instruction
    let instrWithTarget = instruction;
    let isClarificationAnswer = false;
    const prevEntry = pg.chatLog[pg.chatLog.length - 2]; // -2 because we just pushed the user message
    if (prevEntry?.role === 'ai' && prevEntry?.scope === 'clarify') {
      // Search backwards from the clarification for the original user instruction
      for (let j = pg.chatLog.length - 3; j >= 0; j--) {
        if (pg.chatLog[j].role === 'user') {
          instrWithTarget = `${pg.chatLog[j].text} [clarification answer: ${instruction}]`;
          // Carry forward the original target if no new target selected
          if (!targetId && pg.chatLog[j].targetId) {
            targetId = pg.chatLog[j].targetId;
          }
          isClarificationAnswer = true;
          break;
        }
      }
    }

    // Build hint for targeted section (skip when answering clarification — answer resolves ambiguity)
    if (!isClarificationAnswer && targetId && !instrWithTarget.toLowerCase().includes(targetId)) {
      instrWithTarget = `[targeting: ${targetId}] ${instrWithTarget}`;
    }

    const result = await applyInstruction(instrWithTarget, {
      sections,
      validIds,
      getHtml: () => pg.assembled,
      editHistory: globalHistory,
      recentChat,
      onChunk(t) {
        latest = t;
      },
      async refineTokens(note) {
        ST.editingStatus = 'Updating tokens\u2026';
        renderChat();
        const summary = await refineDesignTokens(note);
        const shell = buildDesignShell(ST.concept);
        pg.shellHtml = `${shell.head}\n<!-- SF_SECTIONS -->\n${shell.foot}`;
        pg.assembled = assembledForPage(pg);
        return summary || 'Updated design tokens';
      },
      async refineVision(note) {
        ST.editingStatus = 'Refining vision\u2026';
        renderChat();
        const visionImgs = [];
        if (ST.inspoImg) {
          visionImgs.push({ ...ST.inspoImg, label: 'Inspiration website — current design was calibrated from this' });
        }
        if (ST.siteScreenshot?.length) {
          visionImgs.push({ ...ST.siteScreenshot[0], label: 'Original website screenshot — existing visual identity' });
        }
        const summary = await refineDesignVision(note, visionImgs);
        const shell = buildDesignShell(ST.concept);
        pg.shellHtml = `${shell.head}\n<!-- SF_SECTIONS -->\n${shell.foot}`;
        pg.assembled = assembledForPage(pg);
        return summary || 'Updated design vision';
      },
      async editSection(instr, sectionId, html, onChunk) {
        const sect = pg.sects.find(s => s.id === sectionId);
        ST.editingStatus = `Editing ${sect?.title || sectionId}\u2026`;
        renderChat();
        return applySectionEdit(instr, sectionId, html, onChunk);
      },
      addSection: chatAddSection,
      removeSection: chatRemoveSection,
      async rebuildSection(id, instr) {
        const sect = pg.sects.find(s => s.id === id);
        ST.editingStatus = `Rebuilding ${sect?.title || id}\u2026`;
        renderChat();
        return rebuildSection(id, instr);
      },
    });

    clearInterval(tick);
    ST.activeStreamTicks = ST.activeStreamTicks.filter(t => t !== tick);

    if (result.scope === 'suggest') {
      pg.editUndoStack.pop(); // no change was made
      for (const id of result.suggestIds || []) {
        const sect = pg.sects.find(s => s.id === id);
        if (sect?.status === 'done') {
          ST.editingStatus = `Analyzing ${sect.title}\u2026`;
          renderChat();
          const suggestions = await suggestImprovements(id);
          pg.chatLog.push({
            role: 'suggestions',
            sectionId: id,
            sectionTitle: sect.title,
            suggestions,
            ts: Date.now(),
          });
          renderChat();
        }
      }
    } else if (result.scope === 'clarify') {
      // Detect clarification loop — if last entry was also a clarification, show error instead
      const prevClarify = pg.chatLog[pg.chatLog.length - 2]?.scope === 'clarify';
      if (prevClarify) {
        pg.chatLog.push({
          role: 'error',
          text: 'Could not determine what to edit. Try being more specific, e.g. "translate the About section to Spanish".',
          originalInstr: instruction,
          targetId: targetId || null,
          ts: Date.now(),
        });
      } else {
        pg.chatLog.push({
          role: 'ai',
          scope: 'clarify',
          applied: result.clarification,
          ts: Date.now(),
        });
      }
      pg.editUndoStack.pop(); // no change was made
    } else {
      // Use edit result for initial assembly, then rebuild from sect.html
      // to ensure new <script> blocks from AI edits are included
      if (result.html) {
        pg.assembled = result.html;
      }
      pg.assembled = assembledForPage(pg);
      pg.edited = true;
      finalizeFrame(frameId, pg.assembled);
      // Clean placeholder tokens from summary for user display
      const cleanSummary = (result.summary || '').replace(/__(?:REPLACE_IMG|UPLOADED)_[a-zA-Z0-9_-]+__/g, 'user-provided image');
      pg.editHist.push({ instr: instruction, scope: result.scope, applied: cleanSummary });
      pg.chatLog.push({
        role: 'ai',
        scope: result.scope,
        applied: cleanSummary || 'Applied',
        ts: Date.now(),
      });
      // Scroll to affected section
      const sectionId = result.targets.find(
        t => !(['tokens', 'vision'].includes(t) || t.includes(':')),
      );
      if (sectionId) {
        const el = $(frameId)?.contentDocument?.getElementById(sectionId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  } catch (e) {
    clearInterval(tick);
    if (ST.activeStreamTicks) {
      ST.activeStreamTicks = ST.activeStreamTicks.filter(t => t !== tick);
    }
    // Full restore from undo snapshot
    const snap = pg.editUndoStack.pop();
    if (snap) {
      pg.sects = snap.sects;
      pg.assembled = snap.html;
      pg.shellHtml = snap.shellHtml;
      ST.concept.sections = snap.conceptSections;
      ST.concept.designTokens = snap.tokens;
      ST.concept.designVision = snap.vision;
      ST.concept.colors = snap.colors;
      ST.concept.fonts = snap.fonts;
      syncFlatTokens(ST.concept);
      ST.cachedTokenCtx = '';
    }
    finalizeFrame(frameId, snap?.html || prevHtml);
    pg.chatLog.push({
      role: 'error',
      text: e.message,
      originalInstr: instruction,
      targetId: targetId || null,
      ts: Date.now(),
    });
  } finally {
    ST.isEditing = false;
    ST.editingStatus = '';
    renderChat();
  }
};

// ── UNDO ────────────────────────────────────��────────────────────
/**
 * Undo the last chat edit — restores sections, HTML, shell, design tokens,
 * vision, colors, fonts, and trims chatLog to the snapshot point.
 */
const undoChat = () => {
  const pg = curPage();
  const stack = pg.editUndoStack;
  if (!stack.length || ST.isEditing) {
    return;
  }

  const snap = stack.pop();
  pg.sects = snap.sects;
  pg.assembled = snap.html;
  pg.shellHtml = snap.shellHtml;
  ST.concept.sections = snap.conceptSections;
  ST.concept.designTokens = snap.tokens;
  ST.concept.designVision = snap.vision;
  ST.concept.colors = snap.colors;
  ST.concept.fonts = snap.fonts;
  syncFlatTokens(ST.concept);
  ST.cachedTokenCtx = '';

  // Remove all chatLog entries added since this snapshot
  if (typeof snap.chatLogLen === 'number') {
    pg.chatLog.length = snap.chatLogLen;
  } else {
    // Legacy fallback: remove last user+AI pair
    let removed = 0;
    for (let i = pg.chatLog.length - 1; i >= 0 && removed < 2; i--) {
      if (pg.chatLog[i].role === 'ai' || pg.chatLog[i].role === 'user') {
        pg.chatLog.splice(i, 1);
        removed++;
      }
    }
  }

  // Also pop last editHist entry
  if (pg.editHist.length) {
    pg.editHist.pop();
  }

  finalizeFrame(chatFrameId, pg.assembled);
  renderChat();
};

// ── IFRAME CLICK-TO-SELECT LISTENER ──────────────────────────────
window.addEventListener('message', e => {
  if (e.data?.type === 'sf-select') {
    ST.chatTargetSection = e.data.id;
    if (ST.screen === 'build' && ST.chatTab !== 'chat') {
      ST.chatTab = 'chat';
      renderBuildTabs();
    }
    renderChat();
    ChatTa()?.focus();
  }

  if (e.data?.type === 'sf-text-edit') {
    if (ST.isEditing) {
      return;
    } // Don't mix inline edits with in-flight coordinator edits
    const pg = curPage();
    const sect = pg.sects.find(s => s.id === e.data.sectionId);
    if (!sect) {
      return;
    }

    // Push undo snapshot
    pg.editUndoStack.push({
      html: pg.assembled,
      shellHtml: pg.shellHtml,
      sects: structuredClone(pg.sects),
      conceptSections: structuredClone(ST.concept.sections),
      tokens: structuredClone(ST.concept.designTokens),
      vision: structuredClone(ST.concept.designVision),
      colors: { ...ST.concept.colors },
      fonts: { ...ST.concept.fonts },
      chatLogLen: pg.chatLog.length,
    });
    if (pg.editUndoStack.length > 10) {
      pg.editUndoStack.shift();
    }

    // Update sect.html
    sect.html = cleanModelOutput(e.data.sectionHtml);
    sect.screenshotCache = null;
    sect.suggestions = null;

    // Reassemble page
    pg.edited = false;
    pg.assembled = assembledForPage(pg);

    // Log in chat
    pg.chatLog.push({
      role: 'ai',
      scope: e.data.sectionId,
      applied: 'Text edited inline',
      ts: Date.now(),
    });
    renderChat();
  }
});

// ── MODULE EXPORTS (activate when switching to ES modules) ──────
export {
  applySectionEdit,
  captureSection,
  chatAddSection,
  chatFrameId,
  chatRemoveSection,
  rebuildSection,
  SUGGEST_PROMPT,
  triggerSuggest,
  renderChat,
  SUGGEST_SCHEMA,
  sendChatMessage,
  suggestImprovements,
  undoChat,
};
